import React from "react";
import { Link } from "react-router-dom";
import { Play, Scissors, Film, Users, Share2, Smartphone, Monitor, MessageSquare, UserCircle, Video, Upload, Bell, Calendar } from "lucide-react";

const HeroSection = () => (
  <section data-testid="hero-section" className="page-gradient" style={{
    paddingTop: 140,
    paddingBottom: 80,
    textAlign: 'center',
    position: 'relative',
  }}>
    <div style={{ position: 'relative', zIndex: 1, maxWidth: 720, margin: '0 auto', padding: '0 24px' }}>
      <p className="animate-fade-in-up" style={{
        color: 'var(--cyan)',
        fontSize: 13,
        fontWeight: 600,
        letterSpacing: '0.15em',
        textTransform: 'uppercase',
        marginBottom: 20,
      }}>YOUTH SPORTS VIDEO</p>

      <h1 className="animate-fade-in-up stagger-1" style={{
        fontFamily: "'Outfit', sans-serif",
        fontSize: 'clamp(40px, 7vw, 72px)',
        fontWeight: 800,
        lineHeight: 1.05,
        marginBottom: 24,
        letterSpacing: '-0.02em',
      }}>
        Relive Every<br />Big Play
      </h1>

      <p className="animate-fade-in-up stagger-2" style={{
        color: 'var(--text-secondary)',
        fontSize: 17,
        lineHeight: 1.6,
        marginBottom: 40,
        maxWidth: 520,
        margin: '0 auto 40px',
      }}>
        The complete video platform for youth football. Watch games, create clips, build highlight tapes, and share your best moments.
      </p>

      <div className="animate-fade-in-up stagger-3" style={{
        display: 'flex',
        gap: 14,
        justifyContent: 'center',
        flexWrap: 'wrap',
      }}>
        <Link to="/get-started" data-testid="hero-download-btn" className="btn-primary" style={{
          textDecoration: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '14px 32px',
          fontSize: 15,
        }}>
          <Smartphone size={18} />
          Download App
        </Link>
        <Link to="/get-started" data-testid="hero-signup-btn" className="btn-secondary" style={{
          textDecoration: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '14px 32px',
          fontSize: 15,
        }}>
          <Users size={18} />
          Sign Up League/Org
        </Link>
      </div>
    </div>
  </section>
);

const ScoreboardDemo = () => (
  <section data-testid="demo-section" style={{
    padding: '40px 24px 80px',
    textAlign: 'center',
  }}>
    <p style={{
      color: 'var(--cyan)',
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: '0.15em',
      textTransform: 'uppercase',
      marginBottom: 16,
    }}>TRY IT NOW</p>
    <h2 style={{
      fontFamily: "'Outfit', sans-serif",
      fontSize: 'clamp(28px, 4vw, 42px)',
      fontWeight: 700,
      marginBottom: 12,
    }}>Powerful Video Tools</h2>
    <p style={{
      color: 'var(--text-secondary)',
      fontSize: 15,
      marginBottom: 48,
    }}>Create clips from any moment in the game with our intuitive video player.</p>

    <div className="scoreboard" style={{
      maxWidth: 400,
      margin: '0 auto',
    }}>
      <div style={{
        position: 'absolute',
        top: 12,
        right: 16,
        background: 'var(--cyan)',
        color: '#000',
        fontSize: 10,
        fontWeight: 700,
        padding: '3px 10px',
        borderRadius: 4,
        letterSpacing: '0.05em',
      }}>INTERACTIVE DEMO</div>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 32,
        marginBottom: 8,
      }}>
        <div>
          <p style={{ fontSize: 14, fontWeight: 600, letterSpacing: '0.1em' }}>EAGLES</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span style={{ fontSize: 36, fontWeight: 800, color: 'var(--cyan)', fontFamily: "'Outfit', sans-serif" }}>21</span>
          <span style={{ fontSize: 20, color: 'var(--text-secondary)' }}>-</span>
          <span style={{ fontSize: 36, fontWeight: 800, color: 'var(--cyan)', fontFamily: "'Outfit', sans-serif" }}>14</span>
        </div>
        <div>
          <p style={{ fontSize: 14, fontWeight: 600, letterSpacing: '0.1em' }}>TITANS</p>
        </div>
      </div>
      <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Q3 &bull; 4:32</p>
    </div>
  </section>
);

const featureCards = [
  { icon: Film, title: "Full Game Videos", desc: "Watch complete game footage uploaded by professional operators.", color: "cyan" },
  { icon: Scissors, title: "Create Clips", desc: "Mark key moments and create shareable highlight clips.", color: "orange" },
  { icon: Play, title: "Highlight Tapes", desc: "Compile your best plays into professional highlight reels.", color: "green" },
  { icon: Users, title: "Team Management", desc: "Organize leagues, organizations, teams, and rosters.", color: "purple" },
  { icon: Share2, title: "Easy Sharing", desc: "Share clips on social media with one tap.", color: "cyan" },
];

const FeaturesGrid = () => (
  <section data-testid="features-grid" style={{
    padding: '80px 24px',
    maxWidth: 1200,
    margin: '0 auto',
  }}>
    <div className="glow-line" style={{ marginBottom: 64 }} />
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: 20,
    }}>
      {featureCards.map((f, i) => (
        <div key={f.title} className="glass-card animate-fade-in-up" style={{
          padding: 28,
          opacity: 0,
          animationDelay: `${i * 0.1}s`,
          animationFillMode: 'forwards',
        }}>
          <div className={`icon-box icon-box-${f.color}`} style={{ marginBottom: 16 }}>
            <f.icon size={22} />
          </div>
          <h3 style={{ fontSize: 17, fontWeight: 600, marginBottom: 8 }}>{f.title}</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.6 }}>{f.desc}</p>
        </div>
      ))}
    </div>
  </section>
);

const audiences = [
  { title: "Players & Parents", desc: "Watch games, create highlights, share with recruiters." },
  { title: "Coaches", desc: "Review game footage, share clips with players for training." },
  { title: "Organizations", desc: "Manage teams, rosters, and organizational structure." },
  { title: "Leagues", desc: "Oversee multiple organizations and coordinate events." },
];

const AudienceSection = () => (
  <section data-testid="audience-section" style={{
    padding: '80px 24px',
    maxWidth: 900,
    margin: '0 auto',
    textAlign: 'center',
  }}>
    <p style={{
      color: 'var(--cyan)',
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: '0.15em',
      textTransform: 'uppercase',
      marginBottom: 16,
    }}>WHO IT'S FOR</p>
    <h2 style={{
      fontFamily: "'Outfit', sans-serif",
      fontSize: 'clamp(28px, 4vw, 42px)',
      fontWeight: 700,
      marginBottom: 12,
    }}>Whether you're a player, parent, coach, or league administrator.</h2>
    <p style={{
      color: 'var(--text-secondary)',
      fontSize: 15,
      marginBottom: 48,
    }}>YSV has tools built for every role in youth sports.</p>

    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: 16,
    }}>
      {audiences.map((a, i) => (
        <div key={a.title} className="glass-card" style={{
          padding: 24,
          textAlign: 'left',
        }}>
          <h4 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8, color: 'var(--cyan)' }}>{a.title}</h4>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.6 }}>{a.desc}</p>
        </div>
      ))}
    </div>
  </section>
);

const CTASection = () => (
  <section data-testid="cta-section" style={{
    padding: '80px 24px',
    textAlign: 'center',
  }}>
    <div className="glow-line" style={{ marginBottom: 64 }} />
    <h2 style={{
      fontFamily: "'Outfit', sans-serif",
      fontSize: 'clamp(28px, 4vw, 42px)',
      fontWeight: 700,
      marginBottom: 12,
    }}>Ready to Get Started?</h2>
    <p style={{
      color: 'var(--text-secondary)',
      fontSize: 15,
      marginBottom: 40,
      maxWidth: 500,
      margin: '0 auto 40px',
    }}>Join thousands of teams already using YSV to capture and share their best moments.</p>

    <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
      <Link to="/get-started" data-testid="cta-create-account" className="btn-primary" style={{
        textDecoration: 'none', padding: '14px 32px', fontSize: 15,
      }}>Create Free Account</Link>
      <Link to="/pricing" data-testid="cta-view-pricing" className="btn-secondary" style={{
        textDecoration: 'none', padding: '14px 32px', fontSize: 15,
      }}>View Pricing</Link>
    </div>
  </section>
);

const LandingPage = () => (
  <div data-testid="landing-page">
    <HeroSection />
    <ScoreboardDemo />
    <FeaturesGrid />
    <AudienceSection />
    <CTASection />
  </div>
);

export default LandingPage;
